"use client"

import { useDateLocale } from "../../../lib/locale-context"
import { useTr } from "../../../lib/use-tr"
import { isLikelyTakeover } from "../../../lib/watcher"
import {
  Building2,
  CalendarClock,
  ExternalLink,
  Handshake,
  Store,
  Zap,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { isHttpUrl } from "@/lib/safe-url"

import { Badge } from "@/components/ui/badge"

import { LEADS_PANE_T } from "./translations"
import type {
  WatcherEventSnapshot,
  WatcherEventSourceName,
  WatcherEventType,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

type LeadsPaneKey = keyof typeof LEADS_PANE_T & string

const EVENT_META: Record<
  WatcherEventType,
  { Icon: LucideIcon; labelKey: LeadsPaneKey; dateKey: LeadsPaneKey }
> = {
  creation: {
    Icon: Store,
    labelKey: "watcherEventCreation",
    dateKey: "watcherCreatedOn",
  },
  reprise: {
    Icon: Handshake,
    labelKey: "watcherEventReprise",
    dateKey: "watcherTakenOverOn",
  },
  mise_en_activite: {
    Icon: Zap,
    labelKey: "watcherEventMiseEnActivite",
    dateKey: "watcherActiveSince",
  },
}

const SOURCE_LABEL: Record<WatcherEventSourceName, LeadsPaneKey> = {
  sirene: "watcherSourceSirene",
  bodacc: "watcherSourceBodacc",
}

// An ISO date ("2026-11-12") read as a calendar day: `new Date()` would parse
// it as UTC midnight and show the day before west of Greenwich.
function formatIsoDate(value: string | null, locale: string): string | null {
  if (!value) return null
  const [year, month, day] = value.slice(0, 10).split("-").map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day).toLocaleDateString(locale)
}

// The provenance badges sit in the lead card's wrapping badge row: a badge
// wider than a narrow card wraps its own text instead of being clipped.
const WRAPPING_BADGE = "max-w-full whitespace-normal text-left"

function formatEuros(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value)
}

// The provenance badges of a business-events lead: what happened, when, and
// the takeover's price / previous owner / BODACC notice when known.
export function WatcherEventBadges({ event }: { event: WatcherEventSnapshot }) {
  const tr = useTr(LEADS_PANE_T)
  const dateLocale = useDateLocale()
  // An event type this build does not know reads as a creation.
  const meta = EVENT_META[event.event_type] ?? EVENT_META.creation
  const date = formatIsoDate(event.event_date, dateLocale)
  const dateLabel =
    date === null
      ? null
      : tr(event.is_upcoming ? "watcherUpcomingOn" : meta.dateKey).replace(
          "{date}",
          date,
        )
  const isReprise = event.event_type === "reprise"
  return (
    <>
      <Badge
        tone="primary"
        variant="outline"
        className={`gap-1 ${WRAPPING_BADGE}`}
        title={tr("watcherEventBadgeHint")}
      >
        <meta.Icon className="size-3 shrink-0" />
        {tr(meta.labelKey)}
      </Badge>
      {isLikelyTakeover(event) && (
        <Badge
          tone="primary"
          variant="outline-dashed"
          className={`gap-1 ${WRAPPING_BADGE}`}
          title={tr("watcherLikelyTakeoverHint")}
        >
          <Handshake className="size-3 shrink-0" />
          {tr("watcherLikelyTakeover")}
        </Badge>
      )}
      {dateLabel && (
        <Badge
          tone={event.is_upcoming ? "primary" : "muted"}
          variant="outline"
          className={`gap-1 ${WRAPPING_BADGE}`}
        >
          <CalendarClock className="size-3 shrink-0" />
          {dateLabel}
        </Badge>
      )}
      {isReprise && event.price_eur !== null && (
        <Badge variant="outline" className={`tabular-nums ${WRAPPING_BADGE}`}>
          {tr("watcherSalePrice").replace(
            "{price}",
            formatEuros(event.price_eur, dateLocale),
          )}
        </Badge>
      )}
      {/* A takeover keeps the buyer's flag (its head office), which says
          nothing about the business that changed hands. */}
      {event.event_type === "creation" && event.is_siege === false && (
        <Badge
          tone="muted"
          variant="outline"
          className={`gap-1 ${WRAPPING_BADGE}`}
          title={tr("watcherChainSiteHint")}
        >
          <Building2 className="size-3 shrink-0" />
          {tr("watcherChainSite")}
        </Badge>
      )}
    </>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="text-muted-foreground w-36 shrink-0">{label}</dt>
      <dd className="min-w-0 flex-1 break-words">{children}</dd>
    </div>
  )
}

// The event's label key, for the lead card's accordion row meta: an event
// type this build does not know reads as a creation.
export function watcherEventLabelKey(
  event: WatcherEventSnapshot,
): LeadsPaneKey {
  return (EVENT_META[event.event_type] ?? EVENT_META.creation).labelKey
}

// The registry side of the event, shown in the lead card's accordion like the
// company record: SIREN / SIRET, NAF codes, managers and the sources it was
// read from.
export function WatcherEventDetails({
  event,
}: {
  event: WatcherEventSnapshot
}) {
  const tr = useTr(LEADS_PANE_T)
  const dateLocale = useDateLocale()
  const publishedAt = formatIsoDate(event.published_at, dateLocale)
  const listedAs = event.listed_as?.trim() ?? ""
  const listedSince = formatIsoDate(event.listed_since ?? null, dateLocale)
  const reviews = event.listed_reviews
  const listedReviews =
    typeof reviews === "number"
      ? tr(
          reviews === 1 ? "watcherListedReview" : "watcherListedReviews",
        ).replace("{n}", reviews.toLocaleString(dateLocale))
      : null
  return (
    <dl className="text-2xs space-y-0.5">
      {event.enseigne && (
        <Row label={tr("watcherRowEnseigne")}>{event.enseigne}</Row>
      )}
      {listedAs && (
        <Row label={tr("watcherRowListedAs")}>
          {tr("watcherListedAs").replace("{name}", listedAs)}
          {listedReviews && (
            <span className="text-muted-foreground"> · {listedReviews}</span>
          )}
        </Row>
      )}
      {listedSince && (
        <Row label={tr("watcherRowListedSince")}>
          {tr("watcherListedSince").replace("{date}", listedSince)}
        </Row>
      )}
      {event.siren && <Row label={tr("watcherRowSiren")}>{event.siren}</Row>}
      {event.siret && <Row label={tr("watcherRowSiret")}>{event.siret}</Row>}
      {event.naf && <Row label={tr("watcherRowNaf")}>{event.naf}</Row>}
      {event.naf25 && <Row label={tr("watcherRowNaf25")}>{event.naf25}</Row>}
      {event.activity && (
        <Row label={tr("watcherRowActivity")}>{event.activity}</Row>
      )}
      {event.previous_owner && (
        <Row label={tr("watcherRowPreviousOwner")}>{event.previous_owner}</Row>
      )}
      {isHttpUrl(event.bodacc_url) && (
        <Row label={tr("watcherRowBodacc")}>
          <a
            href={event.bodacc_url}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 hover:underline"
          >
            <ExternalLink className="size-3" />
            {tr("watcherBodaccLink")}
          </a>
        </Row>
      )}
      {event.managers.length > 0 && (
        <Row label={tr("watcherRowManagers")}>
          <ul className="space-y-0.5">
            {event.managers.map((manager, index) => (
              <li key={`${manager.name}-${index}`}>
                <span className="font-medium">{manager.name}</span>
                {manager.role && (
                  <span className="text-muted-foreground">
                    {" "}
                    | {manager.role}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Row>
      )}
      {publishedAt && (
        <Row label={tr("watcherRowPublishedAt")}>{publishedAt}</Row>
      )}
      {event.sources.length > 0 && (
        <Row label={tr("watcherRowSources")}>
          {event.sources
            .map((source) =>
              SOURCE_LABEL[source] ? tr(SOURCE_LABEL[source]) : source,
            )
            .join(", ")}
        </Row>
      )}
    </dl>
  )
}
